import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';

function Table({ headers = [], minCellWidth, tableContent }) {
  const [colSelectedForResizeIdx, setColSelectedForResizeIdx] = useState(null);

  const tableElement = useRef(null);
  const columnRefs = useRef(headers.map(() => React.createRef()));

  useEffect(() => {
    columnRefs.current = headers.map(() => React.createRef());
  }, [headers]);

  const handleColumnResize = useCallback(
    (e) => {
      if (
        colSelectedForResizeIdx === null ||
        !columnRefs.current[colSelectedForResizeIdx]
      )
        return;

      const gridColumns = headers.map((_, i) => {
        const columnRef = columnRefs.current[i]?.current;

        if (i === colSelectedForResizeIdx && columnRef) {
          const width = e.clientX - columnRef.getBoundingClientRect().left;

          if (width >= minCellWidth) {
            return `${width}px`;
          }
        }

        return columnRef ? `${columnRef.offsetWidth}px` : 'auto';
      });

      tableElement.current.style.gridTemplateColumns = `${gridColumns.join(' ')}`;
    },
    [colSelectedForResizeIdx, headers, minCellWidth],
  );

  const handleTouchResize = useCallback(
    (e) => {
      if (!e.touches || e.touches.length === 0) return;

      const touch = e.touches[0];
      handleColumnResize({ clientX: touch.clientX });
    },
    [handleColumnResize],
  );

  const cleanupResizeListeners = useCallback(() => {
    window.removeEventListener('mousemove', handleColumnResize);
    window.removeEventListener('mouseup', endColumnResize);
    window.removeEventListener('touchmove', handleTouchResize);
    window.removeEventListener('touchend', endColumnResize);
  }, [handleColumnResize]);

  const endColumnResize = useCallback(() => {
    setColSelectedForResizeIdx(null);
    cleanupResizeListeners();
  }, [cleanupResizeListeners]);

  useEffect(() => {
    if (colSelectedForResizeIdx !== null) {
      window.addEventListener('mousemove', handleColumnResize);
      window.addEventListener('mouseup', endColumnResize);
      window.addEventListener('touchmove', handleTouchResize);
      window.addEventListener('touchend', endColumnResize);
    }

    return () => {
      cleanupResizeListeners();
    };
  }, [
    colSelectedForResizeIdx,
    handleColumnResize,
    endColumnResize,
    cleanupResizeListeners,
  ]);

  return (
    <Container fluid className="m-0 py-2 px-0">
      <table
        className="custom-vehicle-tracking-table"
        style={{
          gridTemplateColumns: `repeat(${headers.length}, minmax(${minCellWidth || 100}px, 1fr))`,
          minWidth: `${headers.length * (minCellWidth || 100)}px`,
        }}
        ref={tableElement}
      >
        <thead className="table-head-container">
          <tr>
            {headers.map((headerObj, idx) => (
              <th
                className="table-header"
                ref={columnRefs.current[idx]}
                key={`header-${headerObj.label}`}
              >
                <div className="div-flex-row cg-10 div-align-center">
                  {headerObj.isCheckbox ? (
                    <>
                      <input type="checkbox" className="checkbox-grey" />
                      <span className="text-sm text-grey">
                        {headerObj.label}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-grey">{headerObj.label}</span>
                  )}
                </div>
                <button
                  style={{ height: '60%', top: '20%' }}
                  onMouseDown={() => setColSelectedForResizeIdx(idx)}
                  onTouchStart={() => setColSelectedForResizeIdx(idx)}
                  className={`resize-handle ${
                    colSelectedForResizeIdx === idx ? 'active' : 'idle'
                  }`}
                  aria-label={`Resize column ${headerObj.label}`}
                />
              </th>
            ))}
          </tr>
        </thead>
        {tableContent}
      </table>
    </Container>
  );
}

export default Table;
